import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Mail, Phone, Linkedin, Play, Users } from "lucide-react";
import { useSettings } from "../../lib/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamPage() {
  const { settings: s } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/team`).then(r => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const title = `Notre équipe · ${s?.company_name || "Équipe"}`;
  const description = `Découvrez l'équipe de ${s?.company_name || "notre société"} : expertises, parcours et coordonnées.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <section className="relative py-14 md:py-20 bg-gradient-to-b from-[color:var(--brand-navy)]/5 to-transparent">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-3">
              <Users className="w-3.5 h-3.5" /> L'équipe
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-black text-[color:var(--brand-navy)] leading-tight">
              Les visages de {s?.company_short_name || s?.company_name || "notre équipe"}
            </h1>
            <p className="mt-4 text-base md:text-lg text-[hsl(var(--foreground))]/70 max-w-2xl mx-auto">
              Des femmes et des hommes engagés, à votre écoute, prêts à donner vie à vos projets.
            </p>
          </div>

          {loading ? (
            <div className="text-center text-sm text-[hsl(var(--muted-foreground))] py-10">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 border-2 border-dashed rounded-3xl">
              <Users className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-2" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">L'équipe sera bientôt présentée ici.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="team-grid">
              {items.map(m => <TeamCard key={m.id} m={m} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function TeamCard({ m }) {
  const fullName = m.display_last_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.first_name;
  return (
    <article className="group bg-white rounded-3xl overflow-hidden border border-[hsl(var(--border))] shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`team-member-${m.id}`}>
      {/* Media (vidéo longue > avatar vidéo rond > photo > initiales) */}
      {m.video_url ? (
        <TeamVideo url={m.video_url} poster={m.video_poster || m.avatar_url} />
      ) : m.avatar_video_url ? (
        <VideoAvatar url={m.avatar_video_url} hasAudio={m.avatar_video_has_audio} name={fullName} />
      ) : m.avatar_url ? (
        <div className="aspect-[4/5] overflow-hidden bg-[color:var(--brand-navy)]/5">
          <img src={m.avatar_url} alt={fullName} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        </div>
      ) : (
        <div className="aspect-[4/5] bg-gradient-to-br from-[color:var(--brand-sky)]/20 to-[color:var(--brand-navy)]/20 flex items-center justify-center text-[color:var(--brand-navy)] font-display font-black text-6xl">
          {(m.first_name[0] || "?") + (m.display_last_name && m.last_name ? m.last_name[0] : "")}
        </div>
      )}
      <div className="p-5">
        <h3 className="font-display text-xl font-black text-[color:var(--brand-navy)]">{fullName}</h3>
        {m.role && <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--brand-sky)] mt-0.5">{m.role}</div>}
        {m.bio && <p className="mt-3 text-sm text-[hsl(var(--foreground))]/75 leading-relaxed line-clamp-4">{m.bio}</p>}
        {(m.email || m.phone || m.linkedin_url) && (
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
            {m.email && (
              <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--brand-navy)]/5 hover:bg-[color:var(--brand-navy)]/10 text-xs font-semibold text-[color:var(--brand-navy)] transition-colors" data-testid={`team-email-${m.id}`}>
                <Mail className="w-3 h-3" />{m.email}
              </a>
            )}
            {m.phone && (
              <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--brand-sky)]/10 hover:bg-[color:var(--brand-sky)]/20 text-xs font-semibold text-[color:var(--brand-sky)] transition-colors">
                <Phone className="w-3 h-3" />{m.phone}
              </a>
            )}
            {m.linkedin_url && (
              <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 hover:bg-blue-200 text-xs font-semibold text-blue-800 transition-colors">
                <Linkedin className="w-3 h-3" />LinkedIn
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function TeamVideo({ url, poster }) {
  const isEmbed = /youtube\.com\/embed|player\.vimeo\.com/.test(url);
  if (isEmbed) {
    return (
      <div className="aspect-[4/5] bg-black overflow-hidden">
        <iframe src={url} title="Présentation" className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen frameBorder="0" />
      </div>
    );
  }
  return (
    <div className="aspect-[4/5] bg-black overflow-hidden relative">
      <video src={url} poster={poster || undefined} controls playsInline className="w-full h-full object-cover" />
      <div className="absolute top-3 right-3 bg-white/90 rounded-full px-2 py-1 text-[10px] font-bold text-[color:var(--brand-navy)] inline-flex items-center gap-1 pointer-events-none">
        <Play className="w-3 h-3" />Vidéo
      </div>
    </div>
  );
}


function VideoAvatar({ url, hasAudio, name }) {
  const videoRef = React.useRef(null);
  const [muted, setMuted] = React.useState(true);

  const toggleAudio = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const next = !muted;
    setMuted(next);
    videoRef.current.muted = next;
    if (!next) videoRef.current.play().catch(() => {});
  };

  return (
    <div className="aspect-[4/5] overflow-hidden bg-[color:var(--brand-navy)]/5 flex items-center justify-center relative" data-testid="avatar-video-wrapper">
      <div className="w-[78%] aspect-square rounded-full overflow-hidden shadow-xl ring-4 ring-white relative">
        <video
          ref={videoRef}
          src={url}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          aria-label={`Vidéo de présentation de ${name}`}
        />
        {hasAudio && (
          <button
            type="button"
            onClick={toggleAudio}
            className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[color:var(--brand-navy)] shadow-lg backdrop-blur flex items-center justify-center transition-transform hover:scale-110"
            title={muted ? "Activer le son" : "Couper le son"}
            aria-label={muted ? "Activer le son" : "Couper le son"}
            data-testid="avatar-video-audio-toggle"
          >
            {muted ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path></svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
